import { apiTracker } from '../lib/apiTracker';

const MODAL_API_BASE = import.meta.env.VITE_MODAL_API_BASE_URL || '';

export interface UploadResponse {
    success: boolean;
    image_id: string;
    filepath: string;
    message: string;
}

export interface RemoveBackgroundResponse {
    success: boolean;
    image_id: string;
    filepath: string;
    image: string;
    message: string;
}

export interface MockupResponse {
    success: boolean;
    mockup_id: string;
    mockup_path: string;
    image: string;
}

export interface SubmissionResponse {
    success: boolean;
    submissionId: string;
    message: string;
}

/**
 * Extract error message from response text
 */
const extractErrorMessage = (text: string): string => {
    try {
        const errorJson = JSON.parse(text);
        return errorJson.detail || errorJson.message || errorJson.error || text;
    } catch {
        // Not JSON, try to extract meaningful text
        if (text.length > 0) {
            return text.substring(0, 200);
        }
        return 'Unknown error';
    }
};

const handleApiResponse = async (response: Response, endpoint: string) => {
    const startTime = performance.now();
    const requestUrl = response.url;
    const statusCode = response.status;
    const contentType = response.headers.get("content-type") || '';
    
    // Clone response to read text without consuming the stream
    const clonedResponse = response.clone();
    
    // Log response details
    console.log(`[API] ${endpoint} Response:`, {
        url: requestUrl,
        status: statusCode,
        statusText: response.statusText,
        contentType: contentType,
        headers: Object.fromEntries(response.headers.entries()),
        timestamp: new Date().toISOString()
    });
    
    // 1. Try to parse as JSON first (most common case)
    try {
        const data = await response.json();
        const duration = performance.now() - startTime;
        
        // If response is not OK, throw error with message from backend
        if (!response.ok) {
            const errorMsg = data.detail || data.message || data.error || `${endpoint} failed: ${response.status}`;
            console.error(`[API Error] ${endpoint} Details:`, {
                url: requestUrl,
                status: statusCode,
                contentType: contentType,
                error: errorMsg,
                responseData: data,
                timing: `${duration.toFixed(2)}ms`
            });
            throw new Error(errorMsg);
        }
        
        console.log(`[API] ${endpoint} Success:`, {
            timing: `${duration.toFixed(2)}ms`,
            dataKeys: Object.keys(data)
        });
        
        return data;
    } catch (jsonError: any) {
        // If JSON parsing failed, read as text to see what we got
        const text = await clonedResponse.text();
        const duration = performance.now() - startTime;
        
        // 2. Check for HTML response (SPA fallback or wrong URL)
        if (text.trim().startsWith('<!DOCTYPE') || text.trim().startsWith('<html')) {
            console.error(`[API Error] ${endpoint} returned HTML:`, {
                url: requestUrl,
                status: statusCode,
                contentType: contentType,
                bodyPreview: text.substring(0, 500),
                bodyLength: text.length,
                isHTML: true,
                timing: `${duration.toFixed(2)}ms`,
                apiBaseUrl: MODAL_API_BASE
            });
            throw new Error(`API endpoint trả về HTML thay vì JSON. Kiểm tra VITE_MODAL_API_BASE_URL. URL hiện tại: "${MODAL_API_BASE}"`);
        }
        
        // 3. If response is not OK, show error message
        if (!response.ok) {
            const errorMsg = extractErrorMessage(text);
            
            console.error(`[API Error] ${endpoint} Details:`, {
                url: requestUrl,
                status: statusCode,
                statusText: response.statusText,
                contentType: contentType,
                bodyPreview: text.substring(0, 500),
                bodyLength: text.length,
                extractedError: errorMsg,
                timing: `${duration.toFixed(2)}ms`,
                headers: Object.fromEntries(response.headers.entries())
            });
            
            // User-friendly error message in Vietnamese
            let userMessage = `Upload thất bại`;
            if (statusCode === 400) {
                userMessage = `Dữ liệu không hợp lệ. Vui lòng thử lại.`;
            } else if (statusCode === 401 || statusCode === 403) {
                userMessage = `Không có quyền truy cập. Vui lòng kiểm tra lại.`;
            } else if (statusCode === 404) {
                userMessage = `API endpoint không tìm thấy. Kiểm tra cấu hình.`;
            } else if (statusCode === 429) {
                userMessage = `Quá nhiều yêu cầu. Vui lòng thử lại sau.`;
            } else if (statusCode === 503) {
                userMessage = `Dịch vụ tạm thời không khả dụng. Vui lòng thử lại sau.`;
            } else if (statusCode >= 500) {
                userMessage = `Lỗi server. Vui lòng thử lại sau.`;
            } else {
                userMessage = `${userMessage} (${statusCode}): ${errorMsg}`;
            }
            
            throw new Error(userMessage);
        }
        
        // 4. Response is OK but not JSON - this shouldn't happen but handle it
        console.error(`[API Error] ${endpoint} returned non-JSON response:`, {
            url: requestUrl,
            status: statusCode,
            contentType: contentType,
            bodyPreview: text.substring(0, 500),
            bodyLength: text.length,
            timing: `${duration.toFixed(2)}ms`
        });
        throw new Error(`Invalid response format from ${endpoint}. Expected JSON but got: ${contentType || 'unknown'}`);
    }
};

export const modalApi = {
    uploadImage: async (
        imageBase64: string,
        visitorId?: string,
        sessionId?: string,
        imageType?: 'original' | 'mockup' | 'transformed'  // New parameter to specify image type
    ): Promise<UploadResponse> => {
        const url = `${MODAL_API_BASE}/upload_image`;
        const startTime = performance.now();
        
        // Get or generate visitor/session IDs
        if (!visitorId) {
            visitorId = localStorage.getItem('veritas_visitor_id') || crypto.randomUUID();
            localStorage.setItem('veritas_visitor_id', visitorId);
        }
        if (!sessionId) {
            sessionId = localStorage.getItem('veritas_session_id') || crypto.randomUUID();
            localStorage.setItem('veritas_session_id', sessionId);
        }

        const payload = {
            image: imageBase64,
            visitorId: visitorId,
            sessionId: sessionId,
            imageType: imageType || 'original'  // Default to 'original' for backward compatibility
        };
        const payloadSize = JSON.stringify(payload).length;
        const imageSize = imageBase64.length;

        // Log request details
        console.log(`[API] Upload Request:`, {
            url,
            method: 'POST',
            payloadSize: `${(payloadSize / 1024).toFixed(2)}KB`,
            imageSize: `${(imageSize / 1024).toFixed(2)}KB`,
            imageType: imageType || 'original',
            hasApiBase: !!MODAL_API_BASE,
            apiBaseUrl: MODAL_API_BASE || 'NOT SET',
            visitorId: visitorId.substring(0, 8) + '...',
            sessionId: sessionId.substring(0, 8) + '...',
            timestamp: new Date().toISOString()
        });
        
        if (!MODAL_API_BASE) {
            console.warn('[API Warning] VITE_MODAL_API_BASE_URL is not set. Requesting relative path.');
        }

        try {
            const response = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });

            const requestDuration = performance.now() - startTime;
            console.log(`[API] Upload Request completed in ${requestDuration.toFixed(2)}ms`);

            return handleApiResponse(response, 'Upload');
        } catch (networkError: any) {
            const requestDuration = performance.now() - startTime;
            console.error(`[API Error] Upload network error after ${requestDuration.toFixed(2)}ms:`, {
                url,
                error: networkError.message,
                stack: networkError.stack,
                apiBaseUrl: MODAL_API_BASE || 'NOT SET'
            });
            throw new Error(`Không thể kết nối đến server. Kiểm tra kết nối mạng hoặc cấu hình API.`);
        }
    },

    removeBackground: async (imagePath: string, imageId?: string): Promise<RemoveBackgroundResponse> => {
        const url = `${MODAL_API_BASE}/remove_background`;
        const startTime = performance.now();
        
        const payload: any = { image_path: imagePath };
        if (imageId) {
            payload.image_id = imageId;
        }
        
        console.log(`[API] Remove Background Request:`, {
            url,
            method: 'POST',
            payload: {
                image_path: imagePath,
                image_id: imageId || 'none'
            },
            payloadSize: `${(JSON.stringify(payload).length / 1024).toFixed(2)}KB`,
            hasApiBase: !!MODAL_API_BASE,
            timestamp: new Date().toISOString()
        });
        
        try {
            const response = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });

            const requestDuration = performance.now() - startTime;
            console.log(`[API] Remove Background Request completed in ${requestDuration.toFixed(2)}ms`);

            return handleApiResponse(response, 'Remove Background');
        } catch (networkError: any) {
            const requestDuration = performance.now() - startTime;
            console.error(`[API Error] Remove Background network error after ${requestDuration.toFixed(2)}ms:`, {
                url,
                error: networkError.message,
                apiBaseUrl: MODAL_API_BASE || 'NOT SET'
            });
            throw new Error(`Không thể kết nối đến server để xóa nền. Kiểm tra kết nối mạng.`);
        }
    },

    generateMockup: async (
        designImagePath: string,
        templateImageUrl: string,
        removeBackground: boolean = false,
        options?: {
            product_type?: 'phone_case' | 'coffee_cup';
            engraving_style?: 'monochrome' | 'grayscale';
            image_id?: string;
            template_id?: string;
            position?: string;
            style?: string;
            custom_prompt?: string;
            text_overlay?: string;
            font_family?: string;
            print_style?: string;
        }
    ): Promise<MockupResponse> => {
        const url = `${MODAL_API_BASE}/generate_mockup`;
        const startTime = performance.now();
        const payload: any = {
            design_image_path: designImagePath,
            template_image_url: templateImageUrl,
            remove_background: removeBackground,
            product_type: options?.product_type || 'phone_case',
            engraving_style: options?.engraving_style || 'monochrome',
            position: options?.position || 'center',
            style: options?.style || 'default',
            custom_prompt: options?.custom_prompt || '',
            text_overlay: options?.text_overlay || '',
            font_family: options?.font_family || 'Inter',
            print_style: options?.print_style || 'CENTER_STICKER'
        };

        // Only include image_id and template_id if provided
        if (options?.image_id) {
            payload.image_id = options.image_id;
        }
        if (options?.template_id) {
            payload.template_id = options.template_id;
        }

        const payloadSize = JSON.stringify(payload).length;
        console.log(`[API] Generate Mockup Request:`, {
            url,
            method: 'POST',
            payload: {
                design_image_path: designImagePath.substring(0, 50) + '...',
                template_image_url: templateImageUrl.substring(0, 50) + '...',
                remove_background: removeBackground,
                product_type: payload.product_type,
                engraving_style: payload.engraving_style
            },
            payloadSize: `${(payloadSize / 1024).toFixed(2)}KB`,
            hasApiBase: !!MODAL_API_BASE,
            timestamp: new Date().toISOString()
        });

        // Track API request to prevent service worker interruption
        apiTracker.startRequest();

        try {
            if (!MODAL_API_BASE) {
                throw new Error('VITE_MODAL_API_BASE_URL is not configured. Please check environment variables.');
            }

            const response = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });

            const requestDuration = performance.now() - startTime;
            console.log(`[API] Generate Mockup Request completed in ${requestDuration.toFixed(2)}ms`, {
                status: response.status,
                statusText: response.statusText,
                url: response.url
            });

            const data = await handleApiResponse(response, 'Generate Mockup');
            const totalDuration = performance.now() - startTime;
            console.log(`[API] ✅ Mockup generation complete in ${totalDuration.toFixed(2)}ms:`, {
                mockupId: data.mockup_id,
                productType: payload.product_type,
                mockupPath: data.mockup_path
            });
            return data;
        } catch (networkError: any) {
            const requestDuration = performance.now() - startTime;
            const errorDetails = {
                url,
                error: networkError.message,
                apiBaseUrl: MODAL_API_BASE || 'NOT SET',
                payload: {
                    has_design_image_path: !!payload.design_image_path,
                    has_template_image_url: !!payload.template_image_url,
                    has_image_id: !!payload.image_id,
                    has_template_id: !!payload.template_id,
                    product_type: payload.product_type,
                    engraving_style: payload.engraving_style
                },
                stack: networkError.stack
            };
            console.error(`[API Error] Generate Mockup network error after ${requestDuration.toFixed(2)}ms:`, errorDetails);

            // Provide more specific error message
            if (!MODAL_API_BASE) {
                throw new Error('Cấu hình API chưa đúng. Vui lòng kiểm tra VITE_MODAL_API_BASE_URL.');
            }
            if (networkError.message.includes('Failed to fetch') || networkError.message.includes('NetworkError')) {
                throw new Error(`Không thể kết nối đến server để tạo mockup. Kiểm tra kết nối mạng hoặc URL API: ${MODAL_API_BASE}`);
            }
            // If error is already user-friendly (from handleApiResponse), re-throw it
            if (networkError.message.includes('Lỗi server') || networkError.message.includes('Dịch vụ tạm thời')) {
                throw networkError;
            }
            throw new Error(`Lỗi khi tạo mockup: ${networkError.message}`);
        } finally {
            // Always end request tracking, even on error
            apiTracker.endRequest();
        }
    },

    createSubmission: async (data: any): Promise<SubmissionResponse> => {
        const url = `${MODAL_API_BASE}/create_submission`;
        const startTime = performance.now();
        const payloadSize = JSON.stringify(data).length;
        
        console.log(`[API] Create Submission Request:`, {
            url,
            method: 'POST',
            payload: {
                ...data,
                // Truncate long fields for logging
                image_id: data.image_id,
                template_id: data.template_id,
                mockup_id: data.mockup_id
            },
            payloadSize: `${(payloadSize / 1024).toFixed(2)}KB`,
            hasApiBase: !!MODAL_API_BASE,
            timestamp: new Date().toISOString()
        });
        
        try {
            const response = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data),
            });

            const requestDuration = performance.now() - startTime;
            console.log(`[API] Create Submission Request completed in ${requestDuration.toFixed(2)}ms`);

            return handleApiResponse(response, 'Create Submission');
        } catch (networkError: any) {
            const requestDuration = performance.now() - startTime;
            console.error(`[API Error] Create Submission network error after ${requestDuration.toFixed(2)}ms:`, {
                url,
                error: networkError.message,
                apiBaseUrl: MODAL_API_BASE || 'NOT SET'
            });
            throw new Error(`Không thể kết nối đến server để tạo submission. Kiểm tra kết nối mạng.`);
        }
    },

    // Admin endpoints
    exportSubmissions: async (submissionIds: string[], adminUserId: string) => {
        const url = `${MODAL_API_BASE}/export_submissions`;
        console.log(`[API] Exporting submissions at ${url}...`, { submissionIds, adminUserId });
        
        const response = await fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                submission_ids: submissionIds,
                admin_user_id: adminUserId
            }),
        });
        
        return handleApiResponse(response, 'Export Submissions');
    },

    downloadExport: async (exportId: string) => {
        const url = `${MODAL_API_BASE}/download_export/${exportId}`;
        console.log(`[API] Downloading export at ${url}...`);
        
        const response = await fetch(url, {
            method: 'GET',
        });
        
        if (!response.ok) {
            throw new Error(`Download failed: ${response.status}`);
        }
        
        // Return blob for download
        return response.blob();
    },

    uploadTemplate: async (
        file: File,
        metadata: {
            name: string;
            category: string;
            material: string;
            product_type: 'phone_case' | 'coffee_cup';
            is_default?: boolean;
        },
        cleanFile?: File | null,
        maskFile?: File | null
    ) => {
        const url = `${MODAL_API_BASE}/upload_template`;

        // Convert main file to base64
        const base64 = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
                const result = reader.result as string;
                // Remove data URL prefix
                const base64 = result.split(',')[1];
                resolve(base64);
            };
            reader.onerror = reject;
            reader.readAsDataURL(file);
        });

        // Convert clean file to base64 if provided
        let cleanBase64: string | undefined = undefined;
        if (cleanFile) {
            cleanBase64 = await new Promise<string>((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => {
                    const result = reader.result as string;
                    const base64 = result.split(',')[1];
                    resolve(base64);
                };
                reader.onerror = reject;
                reader.readAsDataURL(cleanFile);
            });
        }

        // Convert mask file to base64 if provided
        let maskBase64: string | undefined = undefined;
        if (maskFile) {
            maskBase64 = await new Promise<string>((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => {
                    const result = reader.result as string;
                    const base64 = result.split(',')[1];
                    resolve(base64);
                };
                reader.onerror = reject;
                reader.readAsDataURL(maskFile);
            });
        }

        console.log(`[API] Uploading template at ${url}...`, {
            ...metadata,
            hasCleanImage: !!cleanBase64,
            hasMaskImage: !!maskBase64
        });

        const payload: any = {
            image_base64: base64,
            ...metadata
        };

        if (cleanBase64) {
            payload.clean_image_base64 = cleanBase64;
        }

        if (maskBase64) {
            payload.mask_image_base64 = maskBase64;
        }

        const response = await fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
        });

        return handleApiResponse(response, 'Upload Template');
    },

    updateTemplate: async (templateId: string, updates: {
        name?: string;
        category?: string;
        material?: string;
        is_active?: boolean;
        is_default?: boolean;
        display_order?: number;
        haravan_product_id?: string | null;
        haravan_variant_id?: string | null;
        haravan_product_handle?: string | null;
        new_image?: File | null;
        clean_image?: File | null;
        mask_image?: File | null;
    }) => {
        const startTime = performance.now();

        // If image update is needed, use backend endpoint
        if (updates.new_image || updates.clean_image || updates.mask_image) {
            const url = `${MODAL_API_BASE}/update_template/${templateId}`;

            try {
                const payload: any = { ...updates };

                // Helper to convert file to base64
                const fileToBase64 = (file: File) => new Promise<string>((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onload = () => {
                        const result = reader.result as string;
                        resolve(result.split(',')[1]);
                    };
                    reader.onerror = reject;
                    reader.readAsDataURL(file);
                });

                // Convert new_image
                if (updates.new_image) {
                    const base64 = await fileToBase64(updates.new_image);
                    payload.image_base64 = base64;
                    delete payload.new_image;
                }

                // Convert clean_image
                if (updates.clean_image) {
                    const base64 = await fileToBase64(updates.clean_image);
                    payload.clean_image_base64 = base64;
                    delete payload.clean_image;
                }

                // Convert mask_image
                if (updates.mask_image) {
                    const base64 = await fileToBase64(updates.mask_image);
                    payload.mask_image_base64 = base64;
                    delete payload.mask_image;
                }

                console.log(`[API] Updating template with images at ${url}...`, {
                    templateId,
                    hasNewImage: !!updates.new_image,
                    hasCleanImage: !!updates.clean_image,
                    hasMaskImage: !!updates.mask_image,
                    hasHaravan: !!(updates.haravan_product_id || updates.haravan_variant_id || updates.haravan_product_handle)
                });

                const response = await fetch(url, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload),
                });

                const requestDuration = performance.now() - startTime;
                console.log(`[API] Update Template Request completed in ${requestDuration.toFixed(2)}ms`);

                return handleApiResponse(response, 'Update Template');
            } catch (error: any) {
                const requestDuration = performance.now() - startTime;
                console.error(`[API Error] Update Template error after ${requestDuration.toFixed(2)}ms:`, {
                    url,
                    error: error.message,
                    stack: error.stack,
                    templateId
                });

                if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
                    throw new Error(`Không thể kết nối đến server để cập nhật template. Kiểm tra kết nối mạng hoặc URL API: ${MODAL_API_BASE}`);
                }
                throw error;
            }
        }

        // Otherwise, update directly in Firestore for metadata-only updates
        try {
            console.log(`[API] Updating template metadata in Firestore...`, {
                templateId,
                fields: Object.keys(updates).filter(k => !['new_image', 'clean_image', 'mask_image'].includes(k))
            });

            const { doc, updateDoc } = await import('firebase/firestore');
            const { db } = await import('@/lib/firebase');

            const templateRef = doc(db, 'product_templates', templateId);
            const updateData: any = {
                ...updates,
                updated_at: new Date()
            };
            // Remove image fields if they are null/undefined (though they shouldn't be here if we passed the if check)
            delete updateData.new_image;
            delete updateData.clean_image;
            delete updateData.mask_image;

            await updateDoc(templateRef, updateData);

            const totalDuration = performance.now() - startTime;
            console.log(`[API] ✅ Template metadata updated in ${totalDuration.toFixed(2)}ms`);

            return { success: true, template_id: templateId };
        } catch (error: any) {
            console.error(`[API Error] Firestore update error:`, {
                templateId,
                error: error.message,
                code: error.code
            });
            throw new Error(`Không thể cập nhật template: ${error.message}`);
        }
    },

    deleteTemplate: async (templateId: string) => {
        const url = `${MODAL_API_BASE}/delete_template/${templateId}`;
        console.log(`[API] Deleting template at ${url}...`);
        
        const response = await fetch(url, {
            method: 'DELETE',
        });
        
        return handleApiResponse(response, 'Delete Template');
    },

    // Billing endpoints
    getBillingBalance: async () => {
        const url = `${MODAL_API_BASE}/admin/billing/balance`;
        console.log(`[API] Getting billing balance at ${url}...`);
        
        try {
            const response = await fetch(url, {
                method: 'GET',
            });
            
            return handleApiResponse(response, 'Get Billing Balance');
        } catch (networkError: any) {
            console.error(`[API Error] Get Billing Balance network error:`, {
                url,
                error: networkError.message,
                apiBaseUrl: MODAL_API_BASE || 'NOT SET'
            });
            throw new Error(`Không thể kết nối đến server để kiểm tra số dư. Kiểm tra kết nối mạng hoặc cấu hình API.`);
        }
    },

    depositMoney: async (amount: number, adminUserId: string) => {
        const url = `${MODAL_API_BASE}/admin/billing/deposit`;
        console.log(`[API] Depositing money at ${url}...`, { amount, adminUserId });
        
        const response = await fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                amount,
                admin_user_id: adminUserId
            }),
        });
        
        return handleApiResponse(response, 'Deposit Money');
    },

    getBillingStats: async (startDate?: string, endDate?: string, visitorId?: string) => {
        const url = new URL(`${MODAL_API_BASE}/admin/billing/stats`);
        if (startDate) url.searchParams.append('start_date', startDate);
        if (endDate) url.searchParams.append('end_date', endDate);
        if (visitorId) url.searchParams.append('visitor_id', visitorId);

        console.log(`[API] Getting billing stats at ${url.toString()}...`);

        const response = await fetch(url.toString(), {
            method: 'GET',
        });

        return handleApiResponse(response, 'Get Billing Stats');
    },

    chargeCupMockup: async (data: {
        image_id: string;
        template_id: string;
        visitor_id?: string;
        session_id?: string;
        mockup_path?: string;
    }) => {
        const url = `${MODAL_API_BASE}/charge_cup_mockup`;
        const startTime = performance.now();

        console.log(`[API] Charge Cup Mockup Request:`, {
            url,
            method: 'POST',
            payload: {
                image_id: data.image_id,
                template_id: data.template_id
            },
            timestamp: new Date().toISOString()
        });

        try {
            const response = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data),
            });

            const requestDuration = performance.now() - startTime;
            console.log(`[API] Charge Cup Mockup Request completed in ${requestDuration.toFixed(2)}ms`);

            return handleApiResponse(response, 'Charge Cup Mockup');
        } catch (networkError: any) {
            const requestDuration = performance.now() - startTime;
            console.error(`[API Error] Charge Cup Mockup network error after ${requestDuration.toFixed(2)}ms:`, {
                url,
                error: networkError.message,
                apiBaseUrl: MODAL_API_BASE || 'NOT SET'
            });
            throw new Error(`Không thể kết nối đến server để charge tiền. Kiểm tra kết nối mạng.`);
        }
    }
};